
import React, { useState } from 'react';
import { TrendingUp, Shield, Clock, AlertCircle, CheckCircle2, Download } from 'lucide-react';

const FortiLexReports = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [caseData, setCaseData] = useState({
    caseType: '',
    jurisdiction: '',
    claimAmount: '',
    timeframe: ''
  });
  const [analysis, setAnalysis] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const sampleCases = [
    {
      caseType: 'Banking & Finance Dispute',
      jurisdiction: 'United States (Federal)',
      claimAmount: '15000000',
      timeframe: '24',
      description: 'Major bank breach of syndicated loan agreement'
    },
    {
      caseType: 'Securities Fraud',
      jurisdiction: 'Britain',
      claimAmount: '8500000',
      timeframe: '36', 
      description: 'Public company misrepresentation to investors'
    },
    {
      caseType: 'International Arbitration',
      jurisdiction: 'Singapore',
      claimAmount: '25000000',
      timeframe: '48',
      description: 'Cross-border joint venture dispute'
    }
  ];

  const caseTypes = [
    'Banking & Finance Dispute',
    'Corporate Breach of Contract',
    'Securities Fraud',
    'Commercial Litigation',
    'Breach of Fiduciary Duty',
    'Asset Recovery',
    'International Arbitration'
  ];

  const jurisdictions = [
    'United States (Federal)',
    'Britain',
    'Singapore',
    'Hong Kong',
    'Malaysia',
    'Australia',
    'Canada',
    'EU (International)'
  ];

  const loadSampleCase = (sampleIndex) => {
    const sample = sampleCases[sampleIndex];
    setCaseData(sample);
  };

  const generateDemoAnalysis = () => {
    setCurrentStep(2);
    setIsProcessing(true);

    setTimeout(() => {
      const claimValue = parseInt(caseData.claimAmount);
      const baseViability = caseData.caseType === 'Banking & Finance Dispute' ? 87 :
                           caseData.caseType === 'Securities Fraud' ? 79 :
                           caseData.caseType === 'International Arbitration' ? 92 : 83;
      
      const mockAnalysis = {
        viabilityScore: baseViability,
        winProbability: Math.max(baseViability - 5, 72),
        estimatedDamages: {
          low: Math.floor(claimValue * 0.69),
          mid: Math.floor(claimValue * 0.91),
          high: Math.floor(claimValue * 1.31)
        },
        timelineMonths: parseInt(caseData.timeframe) || 24,
        fundingRecommendation: baseViability >= 85 ? 'RECOMMENDED' : baseViability >= 75 ? 'CONDITIONAL' : 'NOT RECOMMENDED',
        riskFactors: [
          'Comprehensive documentary evidence supporting breach claims',
          'Favorable precedential framework in selected jurisdiction',
          'Defendant demonstrates substantial asset base for recovery',
          'Clear contractual violations with quantifiable financial impact'
        ],
        keyInsights: [
          `Institutional analysis of ${caseData.caseType.toLowerCase()} precedents supports funding viability`,
          `Financial projections align with current litigation funding market parameters`,
          `Jurisdictional selection in ${caseData.jurisdiction} provides strategic litigation advantages`,
          `Risk-adjusted return profile meets sophisticated institutional investor requirements`
        ],
        reportId: `FLX-TEST-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        generatedAt: new Date().toISOString()
      };
      
      setAnalysis(mockAnalysis);
      setCurrentStep(3);
      setIsProcessing(false);
    }, 3500);
  };

  const downloadReport = async () => {
    try {
      const response = await fetch('/.netlify/functions/generate-demo-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysis: analysis,
          caseData: caseData
        })
      });

      if (!response.ok) {
        throw new Error('Report generation failed');
      }

      const htmlContent = await response.text();
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
      }, 1000);

    } catch (error) {
      console.error('Download error:', error);
      alert('Report generation temporarily unavailable');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const resetDemo = () => {
    setCurrentStep(1);
    setAnalysis(null);
    setCaseData({
      caseType: '',
      jurisdiction: '',
      claimAmount: '',
      timeframe: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <div className="mb-6">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="flex justify-center mb-3">
                  <div className="relative">
                    <div className="w-20 h-12 bg-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-600 rounded-t-lg relative shadow-lg">
                      <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center">
                        <div className="w-4 h-4 text-yellow-400">⚖️</div>
                      </div>
                      <div className="absolute -left-3 top-0 w-6 h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-t-sm"></div>
                      <div className="absolute -right-3 top-0 w-6 h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-t-sm"></div>
                      <div className="absolute top-2 left-2 w-2 h-2 bg-gray-800 rounded-sm"></div>
                      <div className="absolute top-2 right-2 w-2 h-2 bg-gray-800 rounded-sm"></div>
                    </div>
                    <div className="w-24 h-3 bg-gradient-to-b from-yellow-600 to-yellow-700 rounded-b-lg -mt-1 mx-auto"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent tracking-wider">
                FortiLex<span className="text-yellow-400 text-2xl align-top">™</span>
              </h1>
              <p className="text-xl text-yellow-500 tracking-widest font-medium mt-1">
                Test Reports Generator
              </p>
            </div>
          </div>
          
          <div className="flex justify-center items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-yellow-500" />
            <h2 className="text-3xl font-bold text-white">
              Professional Sample Reports
            </h2>
          </div>
          <p className="text-xl text-blue-200 max-w-3xl mx-auto mb-8">
            Generate professional litigation funding analysis reports for demonstrations and sales presentations.
          </p>
        </div>

        {currentStep === 1 && (
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {sampleCases.map((sample, index) => (
                <div key={index} 
                     className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg p-6 cursor-pointer hover:from-blue-500/30 hover:to-purple-500/30 transition-all border border-white/10"
                     onClick={() => loadSampleCase(index)}>
                  <h3 className="text-lg font-bold text-white mb-3">{sample.caseType}</h3>
                  <div className="space-y-2 text-blue-200 text-sm">
                    <p><strong>Jurisdiction:</strong> {sample.jurisdiction}</p>
                    <p><strong>Claim:</strong> {formatCurrency(parseInt(sample.claimAmount))}</p>
                    <p><strong>Timeline:</strong> {sample.timeframe} months</p>
                    <p className="text-blue-300 italic">{sample.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Selected Case Analysis</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Case Type</label>
                  <select 
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white"
                    value={caseData.caseType}
                    onChange={(e) => setCaseData(prev => ({...prev, caseType: e.target.value}))}
                  >
                    <option value="">Select case type</option>
                    {caseTypes.map(type => (
                      <option key={type} value={type} className="text-black">{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Jurisdiction</label>
                  <select 
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white"
                    value={caseData.jurisdiction}
                    onChange={(e) => setCaseData(prev => ({...prev, jurisdiction: e.target.value}))}
                  >
                    <option value="">Select jurisdiction</option>
                    {jurisdictions.map(jurisdiction => (
                      <option key={jurisdiction} value={jurisdiction} className="text-black">{jurisdiction}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Claim Amount (USD)</label>
                  <input 
                    type="number"
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white"
                    placeholder="e.g., 5000000"
                    value={caseData.claimAmount}
                    onChange={(e) => setCaseData(prev => ({...prev, claimAmount: e.target.value}))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Expected Timeline</label>
                  <select 
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white"
                    value={caseData.timeframe}
                    onChange={(e) => setCaseData(prev => ({...prev, timeframe: e.target.value}))}
                  >
                    <option value="">Select timeframe</option>
                    <option value="12" className="text-black">12-18 months</option>
                    <option value="24" className="text-black">18-24 months</option>
                    <option value="36" className="text-black">2-3 years</option>
                    <option value="48" className="text-black">3+ years</option>
                  </select>
                </div>
              </div>

              <button
                onClick={generateDemoAnalysis}
                disabled={!caseData.caseType || !caseData.jurisdiction || !caseData.claimAmount}
                className="w-full mt-8 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 disabled:bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
              >
                Generate Professional Analysis Report
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && isProcessing && (
          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center">
            <div className="animate-spin w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-white mb-4">Generating Institutional-Grade Analysis</h2>
            <p className="text-blue-200 mb-6">
              FortiLex™ AI analyzing litigation patterns and market dynamics...
            </p>
          </div>
        )}

        {currentStep === 3 && analysis && (
          <div className="max-w-6xl mx-auto space-y-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-white mb-6">FortiLex™ Litigation Funding Analysis</h2>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-blue-300 font-medium text-center">
                  Report ID: {analysis.reportId} | Generated: {new Date().toLocaleString()}
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-lg p-6 text-center">
                  <TrendingUp className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white">{analysis.viabilityScore}%</div>
                  <div className="text-green-400 font-medium">Funding Viability</div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-6 text-center">
                  <CheckCircle2 className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white">{analysis.winProbability}%</div>
                  <div className="text-blue-400 font-medium">Win Probability</div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg p-6 text-center">
                  <Clock className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white">{analysis.timelineMonths}</div>
                  <div className="text-purple-400 font-medium">Months Est.</div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-green-300 mb-3">
                  Recommendation: {analysis.fundingRecommendation}
                </h3>
                <p className="text-green-200">
                  Professional analysis demonstrates strong institutional funding potential based on comprehensive legal and financial assessment.
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Financial Projections</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="text-sm text-red-300 mb-2">Conservative</div>
                  <div className="text-2xl font-bold text-red-400">{formatCurrency(analysis.estimatedDamages.low)}</div>
                </div>
                <div className="text-center bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="text-sm text-yellow-300 mb-2">Most Likely</div>
                  <div className="text-3xl font-bold text-yellow-400">{formatCurrency(analysis.estimatedDamages.mid)}</div>
                </div>
                <div className="text-center bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="text-sm text-green-300 mb-2">Optimistic</div>
                  <div className="text-2xl font-bold text-green-400">{formatCurrency(analysis.estimatedDamages.high)}</div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">Strength Factors</h3>
                <div className="space-y-3">
                  {analysis.riskFactors.map((factor, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-blue-200 text-sm">{factor}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">Strategic Insights</h3>
                <div className="space-y-3">
                  {analysis.keyInsights.map((insight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-blue-200 text-sm">{insight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-6 text-center">
              <div className="flex justify-center gap-4">
                <button 
                  onClick={downloadReport}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-3 px-8 rounded-lg font-semibold transition-all flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download PDF Report
                </button>
                <button 
                  onClick={resetDemo}
                  className="bg-gray-600 hover:bg-gray-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors"
                >
                  New Analysis
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 pt-8 border-t border-yellow-500/20 text-center">
          <div className="space-y-3 text-yellow-200/80 text-sm">
            <p className="font-medium text-yellow-300">August 2025 Updated with Anthropic Claude Sonnet 4</p>
            <p className="text-yellow-200">© 2025 FortiLex™ Global Litigation Analyzer. The Software Suite™ and A.J. Hakimi, LLB (Hons) [Wolverhampton], CLP (Hons) [LPQB], CMSA [CFI Vancouver]. All Rights Reserved.</p>
            <p className="text-yellow-300">Email: the.software.suite@protonmail.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FortiLexReports;